import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { GatewayInstance, Payment } from '@vsolv/packages/payments/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { PaymentService } from '../../services';
import { RemovePaymentMethodDialog } from './remove-payment-method.dialog';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-update-billing-info-dialog',
  templateUrl: './update-billing-info.dialog.html',
  standalone: false,
})
export class UpdateBillingInfoDialog {
  @HostBinding('class') protected _classes = 'contents';

  private toastSvc = inject(ToastService);
  private formBuilder = inject(FormBuilder);
  private paymentSvc = inject(PaymentService);

  @ViewChild('dialog') private dialog?: DialogComponent;
  @ViewChild('removePaymentMethodDialog') private removePaymentMethodDialog?: RemovePaymentMethodDialog;

  @Output() saved = new EventEmitter<Payment.ChangePaymentMethodResponse | Payment.RemovePaymentMethodResponse>();

  protected _payment?: Payment.Model;
  @Input() set payment(payment: Payment.Model | undefined) {
    this._payment = payment;
    this.reset();
  }

  protected control = this.formBuilder.control(
    { value: this.payment?.paymentMethodId ?? null, disabled: false },
    Validators.required
  );

  protected saving = false;

  protected get gatewayInstanceId(): string | null {
    if (!this._payment) return null;
    if (this._payment.gatewayInstanceId) return this._payment.gatewayInstanceId;
    return this._payment.gatewayRoutingReason === GatewayInstance.RoutingReason.LEGACY_COMPATIBILITY
      ? GatewayInstance.LEGACY_AUTHORIZE_NET_INSTANCE_ID
      : null;
  }

  open() {
    this.reset();
    this.dialog?.open();
  }

  close() {
    this.dialog?.close();
  }

  protected openRemovePaymentMethodDialog() {
    this.removePaymentMethodDialog?.open();
  }

  protected closeRemovePaymentMethodDialog() {
    this.removePaymentMethodDialog?.close();
  }

  protected async save() {
    this.saving = true;

    try {
      const paymentId = this._payment?.id;
      if (!paymentId) throw new Error('Unable to update billing details.');

      const paymentMethodId = this.control.value;
      if (!paymentMethodId) throw new Error('Unable to update billing details.');

      const currentPaymentMethodId = this._payment?.paymentMethodId ?? null;
      if (paymentMethodId === currentPaymentMethodId) {
        throw new Error('Make sure to select a different payment method or add a new one.');
      }

      const result = await this.paymentSvc.changePaymentMethod(paymentId, paymentMethodId);
      this.saved.emit(result);

      this.toastSvc.show({
        type: 'success',
        title: 'Billing details updated',
        text: 'The payment method has been updated',
      });

      this.close();
    } catch (err) {
      console.error(err);
      this.toastSvc.show({
        title: 'Unable to update billing details',
        type:
          err instanceof Error && err.message === 'Make sure to select a different payment method or add a new one.'
            ? 'warning'
            : 'error',
        text: err instanceof Error ? err.message : 'Unknown Error',
      });
    } finally {
      this.saving = false;
    }
  }

  protected async removePaymentMethod() {
    this.saving = true;

    try {
      const paymentId = this._payment?.id;
      if (!paymentId) throw new Error('Unable to remove payment details.');

      const result = await this.paymentSvc.removePaymentMethod(paymentId);
      this.saved.emit(result);

      this.toastSvc.show({
        type: 'success',
        title: 'Payment details removed',
        text: 'Future automatic billing attempts will fail until a new payment method is added.',
      });

      this.closeRemovePaymentMethodDialog();
      this.close();
    } catch (err) {
      console.error(err);
      this.toastSvc.show({
        title: 'Unable to remove payment details',
        type: 'error',
        text: err instanceof Error ? err.message : 'Unknown Error',
      });
    } finally {
      this.saving = false;
    }
  }

  private reset() {
    this.control.reset({ value: this._payment?.paymentMethodId ?? null, disabled: false });
  }
}
